// Imports for external libraries go here.
import React, { FC, useCallback, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { CheckBox, Heading, Types, getEventKeyTypes, size, tags } from '@marriott/mi-ui-library';
import { isNonEmptyString } from '@marriott/shared/mi-helper-utils';
import { LegalTextWithLink } from '../LegalTextWithLink';
import { LinkAccordionBody } from '../TermModalBody';
import { StyledConsentCheckListItem } from './ConsentCheckListItem.styles';
import { ConsentCheckListItemProps } from './ConsentCheckListItem.types';
import { TermsLinkComponent } from './TermsLinkComponent';

export const ConsentCheckListItem: FC<ConsentCheckListItemProps> = props => {
  const {
    title,
    titleClassName,
    titleClassNameOverride,
    label,
    errorLabel,
    termsBtnLabel,
    termsLink,
    selectedConsentId,
    checkboxLabelContainerClassName,
    checkboxInnerLabelClassName,
    checkboxContainerClassName,
    className,
    name,
    isChecked,
    isDisabled,
    onModalOpen,
    onChange,
    onLinkClickInCheckbox,
    linkClass,
    linkTarget,
    errorMessageClassName,
    validateErrors,
    showCheckbox,
    linkBehavior,
    closeAccordionBtnLabel,
    openAccordionBtnLabel,
    sectionType,
  } = props;
  const [accordionOpen, setAccordionOpen] = useState(false);

  const showError = validateErrors && !isChecked;

  const onCheckboxChange = () => {
    onChange(selectedConsentId, name, sectionType);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLLabelElement>) => {
    if (getEventKeyTypes(event)?.isEnterKey && event.currentTarget instanceof HTMLInputElement) {
      event.preventDefault();
      onCheckboxChange();
    }
  };

  const onTermsModalOpen = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onModalOpen(e, termsLink!);
    },
    [termsLink, onModalOpen] // Dependencies to ensure function isn't recreated unnecessarily
  );

  const toggleState =
    (setState: React.Dispatch<React.SetStateAction<boolean>>) => (e: React.ChangeEvent | React.MouseEvent) => {
      e.stopPropagation();
      setState(prev => !prev);
    };

  const isDefaultTermsLink = !!(termsLink && termsBtnLabel && linkBehavior === 'default');
  const isNewTabTermsLink = !!(termsLink && termsBtnLabel && linkBehavior === 'newTab');
  const shouldDisplayShowMoreButton = !!(termsLink && linkBehavior === 'accordion' && !accordionOpen);

  return (
    <>
      <StyledConsentCheckListItem
        onClick={onLinkClickInCheckbox} // Attach click handler to all the anchor tags present within the checkbox label
        data-testid="legal-ConsentCheckListItem"
        data-component-name="m-legal-ConsentCheckListItem"
        className={checkboxContainerClassName ?? ''}
      >
        {showCheckbox ? (
          <CheckBox
            checkboxId={name}
            checkboxName={name}
            className={className ? className : ''}
            checked={isChecked}
            disabled={isDisabled}
            onChange={onCheckboxChange}
            onKeyDown={handleKeyDown}
          >
            {isNonEmptyString(title) ? (
              <Heading
                variation={Types.headingType.title}
                fontSize={size.extraSmall}
                element={tags.h4}
                titleText={title ?? ''}
                disableCustomClickTrack={true}
                customClass={titleClassNameOverride ?? titleClassName ?? ''}
              />
            ) : null}
            <LegalTextWithLink
              descriptionText={label}
              className={checkboxLabelContainerClassName || ''}
              innerChildClassName={checkboxInnerLabelClassName || ''}
            />
            {showError && errorLabel && (
              <LegalTextWithLink descriptionText={errorLabel} className={errorMessageClassName} />
            )}
          </CheckBox>
        ) : (
          <>
            <LegalTextWithLink
              descriptionText={label}
              className={checkboxLabelContainerClassName || ''}
              innerChildClassName={checkboxInnerLabelClassName || ''}
            />
            {showError && errorLabel && (
              <LegalTextWithLink descriptionText={errorLabel} className={errorMessageClassName} />
            )}
          </>
        )}
        <TermsLinkComponent
          termsLink={termsLink}
          termsBtnLabel={termsBtnLabel}
          openAccordionBtnLabel={openAccordionBtnLabel}
          linkClass={linkClass}
          target={linkTarget}
          onLinkClick={onTermsModalOpen}
          onAccordionToggle={toggleState(setAccordionOpen)}
          isDefaultTermsLink={isDefaultTermsLink}
          isNewTabTermsLink={isNewTabTermsLink}
          shouldDisplayShowMoreButton={shouldDisplayShowMoreButton}
        />
      </StyledConsentCheckListItem>
      {accordionOpen ? (
        <LinkAccordionBody
          closeAccordionBtnLabel={closeAccordionBtnLabel}
          href={termsLink!}
          handleClose={toggleState}
          linkClass={linkClass}
          setAccordionOpen={setAccordionOpen}
        />
      ) : (
        <></>
      )}
    </>
  );
};
